import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

/** Detail payload of the `twasta:agent-files-changed` window event. */
export interface AgentFilesChangedDetail {
  project?: string
  /** Every path the run touched (written OR deleted). */
  files?: string[]
  /** The subset that was DELETED — always a subset of `files`. */
  deleted?: string[]
}

/**
 * Auto-refresh signal for design surfaces after an AI agent run reports it
 * touched project files (the `twasta:agent-files-changed` window event,
 * dispatched by AskAIDock / useAutoAgent).
 *
 * Returns a counter the consumer includes in its data-load effect's
 * dependency list — every bump forces a refetch of the open object so the
 * agent's edits show up without a hard browser refresh.
 *
 * Dirty-state guard: when the editor has unsaved changes the refresh is NOT
 * applied immediately (it would clobber in-progress edits). Instead it's
 * deferred and applied as soon as `isDirty` goes false again (save or
 * discard); a toast tells the author why the surface didn't move yet.
 *
 * Project matching is conservative — same as TransactionPreviewPane: refetch
 * on any event for this project (or with no project info). The agent doesn't
 * reliably report which objects it touched (`files` is often empty), and a
 * spare refetch of one JSON object is cheap; stale UI is not.
 */
export function useAgentRefresh(project: string | undefined, isDirty: boolean): number {
  const [nonce, setNonce] = useState(0)

  // Refs keep the listener identity stable across renders while the handler
  // always reads the latest dirty/project state.
  const dirtyRef = useRef(isDirty)
  dirtyRef.current = isDirty
  const projectRef = useRef(project)
  projectRef.current = project
  const pendingRef = useRef(false)

  useEffect(() => {
    const onFilesChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail as AgentFilesChangedDetail | undefined
      const evProj = detail?.project
      if (evProj && projectRef.current && evProj !== projectRef.current) return
      if (dirtyRef.current) {
        pendingRef.current = true
        toast(
          'The AI agent updated project files. This object will refresh once you save or discard your edits.',
          { id: 'agent-refresh-deferred', icon: '🤖', duration: 6000 },
        )
        return
      }
      setNonce((n) => n + 1)
    }
    window.addEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
    return () =>
      window.removeEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
  }, [])

  // Apply a deferred refresh as soon as the editor is clean again.
  useEffect(() => {
    if (!isDirty && pendingRef.current) {
      pendingRef.current = false
      setNonce((n) => n + 1)
    }
  }, [isDirty])

  return nonce
}

/**
 * Deletion-aware companion to {@link useAgentRefresh}.
 *
 * A refetch is the right response to a CHANGED object and the wrong response to
 * a DELETED one: the GET 404s and the surface goes on showing an object that no
 * longer exists (or, worse, a later save re-creates it). Deletions used to be
 * invisible here because the backend's snapshot diff only ever reported files
 * present in the AFTER snapshot — fixed in Phase 0.8 of
 * docs/ssh_agent_console_support_plan.md, which is what makes this hook
 * possible.
 *
 * Usage in a designer that has one object open:
 *
 *   const agentRefresh = useAgentRefresh(projectName, isDirty)
 *   useAgentDeletion(projectName, objName, 'visual_models', () => {
 *     navigate('/workbench/analytics')     // or clear local state
 *   })
 *
 * `modelDir` is matched as a path fragment (e.g. `visual_models`), and the
 * object name against both `model_<obj>.json` and `<obj>.json` — the two
 * spellings the model folders use.
 */
export function useAgentDeletion(
  project: string | undefined,
  objName: string | undefined,
  modelDir: string,
  onDeleted: (path: string) => void,
): void {
  const projectRef = useRef(project)
  projectRef.current = project
  const objRef = useRef(objName)
  objRef.current = objName
  const dirRef = useRef(modelDir)
  dirRef.current = modelDir
  const cbRef = useRef(onDeleted)
  cbRef.current = onDeleted

  const matches = useCallback((path: string): boolean => {
    const obj = (objRef.current || '').trim().toLowerCase()
    const dir = (dirRef.current || '').trim().toLowerCase()
    if (!obj) return false
    const rel = path.replace(/\\/g, '/').toLowerCase()
    if (dir && !rel.includes(dir)) return false
    const base = rel.split('/').pop() || ''
    return base === `model_${obj}.json` || base === `${obj}.json`
  }, [])

  useEffect(() => {
    const onFilesChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail as AgentFilesChangedDetail | undefined
      const evProj = detail?.project
      if (evProj && projectRef.current && evProj !== projectRef.current) return
      const deleted = detail?.deleted || []
      if (!deleted.length) return
      const hit = deleted.find(matches)
      if (!hit) return
      toast(
        `The AI agent deleted "${objRef.current}". Closing it — it no longer exists in this project.`,
        { id: 'agent-object-deleted', icon: '🗑️', duration: 8000 },
      )
      cbRef.current(hit)
    }
    window.addEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
    return () =>
      window.removeEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
  }, [matches])
}
